import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Wallet, 
  ChartBar,
  Package,
  ClipboardText,
  FileText,
  Receipt,
  Tag,
  ListChecks,
  ArrowLeft,
  ArrowRight,
  Scissors,
  Circle,
  Archive
} from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';

const gestaoModules = [
  {
    id: 'clientes',
    title: 'Clientes',
    description: 'Cadastro, ficha viva, histórico técnico e vínculo com o cliente.',
    icon: Users,
    path: '/gestao/clientes',
    color: '#1B4332',
    active: true
  },
  {
    id: 'pacman',
    title: 'PAC MAN',
    description: 'Estoque, movimentações, compras e custo real por serviço.',
    icon: Package,
    path: '/gestao/pacman',
    color: '#52B788',
    active: true
  },
  {
    id: 'cod',
    title: 'COD',
    description: 'Controle Operacional de Disponibilidade — painel operacional e grade holística.',
    icon: ClipboardText,
    path: '/gestao/cod',
    color: '#74C69D',
    active: true
  },
  {
    id: 'comandas',
    title: 'Comandas',
    description: 'Registre serviços e produtos realizados antes do recebimento no caixa.',
    icon: Receipt,
    path: '/gestao/comandas',
    color: '#40916C',
    active: true
  },
  {
    id: 'servicos',
    title: 'Serviços',
    description: 'Cadastre serviços, códigos e valores base usados nas comandas.',
    icon: Tag,
    path: '/gestao/servicos',
    color: '#52B788',
    active: true
  },
  {
    id: 'caixa',
    title: 'Caixa',
    description: 'Abertura, movimentações, sangria, suprimento e fechamento do dia.',
    icon: Wallet,
    path: '/gestao/caixa',
    color: '#2D6A4F',
    active: true
  },
  {
    id: 'producao',
    title: 'Produção',
    description: 'Leitura da execução real do salão: entradas, serviços, produtos e clientes.',
    icon: ChartBar,
    path: '/gestao/producao',
    color: '#40916C',
    active: true,
    adminOnly: true
  },
  {
    id: 'afo',
    title: 'AFO',
    description: 'Agente de Fechamento Operacional — apuracao mensal padronizada.',
    icon: FileText,
    path: '/gestao/afo',
    color: '#95D5B2',
    active: true
  },
  {
    id: 'tarefas',
    title: 'Tarefas',
    description: 'Gestão de lembretes, compromissos e pendências importantes da rotina do salão.',
    icon: ListChecks,
    path: '/gestao/tarefas',
    color: '#74C69D',
    active: true
  },
  {
    id: 'historico',
    title: 'Histórico',
    description: 'Área ADMIN para consulta de dados importados de sistemas anteriores (somente leitura).',
    icon: Archive,
    path: '/gestao/historico',
    color: '#6B21A8',
    active: true,
    adminOnly: true,
  }
];

export const GestaoSubHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toUpperCase() === 'ADMIN';
  const visibleModules = gestaoModules.filter(m => !m.adminOnly || isAdmin);

  const handleBack = () => {
    navigate('/');
  };

  const handleModuleClick = (module) => {
    navigate(module.path);
  };

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="gestao-page">
      <div className="salonup-container">
        {/* Header */}
        <header className="salonup-header animate-fade-in">
          <div className="salonup-brand">
            <div className="salonup-logo-mark">
              <Scissors size={22} weight="bold" />
            </div>
            <span className="salonup-logo-text">
              Salon<span>Up</span>
            </span>
          </div>
          
          <button 
            onClick={handleBack}
            data-testid="btn-back"
            className="salonup-btn-back"
          >
            <ArrowLeft size={18} weight="bold" />
            <span>Voltar</span>
          </button>
        </header>

        {/* Title Section */}
        <section className="mb-10 md:mb-12 animate-fade-in-up opacity-0 delay-100" style={{ animationFillMode: 'forwards' }}>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <h1 className="salonup-heading-lg">
              Gestão
            </h1>
            <span className="salonup-badge">CORE</span>
            <div className="flex items-center gap-1.5 ml-1">
              <div className="salonup-status-dot" />
              <span className="text-[10px] font-medium text-[#71717A] uppercase tracking-wide">Ativo</span>
            </div>
          </div>
          <p className="salonup-text-body">
            Escolha a função que deseja acessar
          </p>
        </section>

        {/* Modules Grid */}
        <section className="salonup-grid-modules">
          {visibleModules.map((module, index) => {
            const Icon = module.icon;
            const isActive = module.active;
            
            return (
              <div
                key={module.id}
                data-testid={`card-${module.id}`}
                onClick={() => handleModuleClick(module)}
                className={`salonup-card p-6 min-h-[200px] animate-fade-in-up opacity-0 ${!isActive ? 'opacity-60 cursor-default' : ''}`}
                style={{ animationDelay: `${150 + (index * 80)}ms`, animationFillMode: 'forwards' }}
              >
                <div className="flex flex-col h-full justify-between relative z-10">
                  {/* Top section */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div 
                        className="salonup-icon-wrapper salonup-icon-wrapper-sm"
                        style={{ backgroundColor: `${module.color}10` }}
                      >
                        <Icon size={22} weight="regular" style={{ color: module.color }} />
                      </div>
                      {isActive ? (
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          <span className="text-[9px] font-semibold text-green-700 uppercase tracking-wider">Ativo</span>
                        </div>
                      ) : (
                        <span className="text-[9px] font-semibold text-[#A1A1AA] uppercase tracking-wider border border-[#E4E4E7] px-2 py-0.5 rounded-full">Em breve</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Bottom section */}
                  <div>
                    <h3 className="salonup-heading-md mb-1.5">
                      {module.title}
                    </h3>
                    <p className="salonup-text-sm mb-4">
                      {module.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      {isActive ? (
                        <>
                          <span className="salonup-label">Acessar</span>
                          <ArrowRight 
                            size={18} 
                            weight="bold" 
                            className="salonup-arrow" 
                          />
                        </>
                      ) : (
                        <span className="salonup-label text-[#A1A1AA]">Em desenvolvimento</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Footer */}
        <footer className="salonup-footer animate-fade-in opacity-0" style={{ animationDelay: '700ms', animationFillMode: 'forwards' }}>
          <p className="salonup-text-xs">
            SalonUp — O sistema operacional do salão moderno
          </p>
          <p className="salonup-text-xs">
            Gestão (CORE) · 8 módulos ativos · 1 em desenvolvimento
          </p>
        </footer>
      </div>
    </div>
  );
};

export default GestaoSubHome;
